import { Box, Divider, Stack, Typography } from '@mui/material';
import uniqBy from 'lodash/uniqBy';
import React, { useMemo } from 'react';

import { useFetchData } from '../../../hooks/useFetchData';
import { DataRequestItem, QuestionType } from '../../../Types/dealGPT';
import { dataRequestItemQuestionsPathUrl } from '../../../util/routesUrls';
import { ExpandableList, ExpandableListLinkItem } from '../../ExpandableList';
import { DataRequestItemNotes } from './DataRequestItemNotes';
import { DataRequestItemPrioritySelect } from './DataRequestItemPrioritySelect';
import { DataRequestItemStatusSelect } from './DataRequestItemStatusSelect';

export interface DataRequestItemOverviewProps {
  item: DataRequestItem;
  onItemChanged: (newItem: DataRequestItem) => void;
}

const DataRequestItemOverview: React.FC<DataRequestItemOverviewProps> = ({
  item,
  onItemChanged,
}) => {
  const { data: applicableQuestions } = useFetchData<QuestionType[]>({
    urlPath: dataRequestItemQuestionsPathUrl(item.projectId, item.id),
  });

  const sources = useMemo(() => {
    const docs = applicableQuestions?.flatMap((q) => q.documents || []);
    const uniqueDocs = uniqBy(docs || [], 'fileName');

    return uniqueDocs.map(
      (d) => ({ label: d.fileName, href: d.link } satisfies ExpandableListLinkItem)
    );
  }, [applicableQuestions]);

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Box display="flex" flexDirection="column" gap={1}>
        <Typography variant="h5">Details</Typography>
        <Typography>{item.details}</Typography>
      </Box>

      <Divider />
      <Box display="flex" flexDirection="row" justifyContent="space-between">
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6" sx={{ color: '#656575' }}>
            Status:
          </Typography>
          <DataRequestItemStatusSelect item={item} onItemChanged={onItemChanged} />
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6" sx={{ color: '#656575' }}>
            Priority:
          </Typography>
          <DataRequestItemPrioritySelect item={item} onItemChanged={onItemChanged} />
        </Stack>
      </Box>
      <DataRequestItemNotes item={item} onItemChanged={onItemChanged} />
      <ExpandableList title="Sources" items={sources} mode="bullet" />
      <ExpandableList
        title="List Of Applicable Questions"
        items={applicableQuestions?.map((q) => q.question) || []}
        mode="bullet"
      />
    </Box>
  );
};

export default DataRequestItemOverview;
